import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8081";

interface ICP {
  filename: string;
  profile_name: string;
  profile_type: string;
  eligibility_status: string;
  nationality: string;
  application_type: string;
}

interface AgentVersion {
  id: string;
  version: number;
  agent_name: string;
  stage: string;
}

interface Message {
  id?: number; // Database ID (for duplicate detection)
  role: 'user' | 'agent';
  message: string;
  timestamp: string;
  turn_number: number;
  turn_range?: [number, number]; // [start, end] turn numbers for merged chunks
  chunk_count?: number; // Number of chunks merged
  message_ids?: number[]; // Track database IDs of merged chunks to prevent duplicates
}

interface TestSession {
  session_id: string;
  started_at: string;
  status: string;
  profile: {
    name: string;
    type: string;
    eligibility: string;
  };
}

interface BatchInfo {
  batch_number: string;
  agent_version_id: string;
  tenant_id: string;
  total_tests: number;
  status: string;
  message: string;
}

interface BatchStats {
  batch_number: string;
  session_count: number;
  first_session: string;
  last_session: string;
  completed_count: number;
  appointment_count: number;
}

interface ActiveBatch {
  batch_number: string;
  active_sessions: number;
  total_sessions: number;
  started_at: string;
  first_completed_at: string;
  completed_count: number;
  sample_profile_name: string;
}

export function ICPTesting() {
  // State
  const [icps, setIcps] = useState<ICP[]>([]);
  const [agents, setAgents] = useState<AgentVersion[]>([]);
  const [selectedICP, setSelectedICP] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(() => {
    const saved = localStorage.getItem('icp-test-timeout');
    return saved ? parseInt(saved) : 600;
  });
  const [timeoutInput, setTimeoutInput] = useState<string>(() => {
    const saved = localStorage.getItem('icp-test-timeout');
    return saved || '600';
  });
  const [saveMessage, setSaveMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState(true);

  // ICP Selection State
  const [icpSelections, setIcpSelections] = useState<Record<string, boolean>>({});
  const [showICPSelectionModal, setShowICPSelectionModal] = useState(false);
  const [icpSelectionsLoaded, setIcpSelectionsLoaded] = useState(false);

  // ICP Client Agent Configuration
  const [icpClientSystemPrompt, setIcpClientSystemPrompt] = useState(`You are roleplaying as {ICP_NAME}, a real person seeking immigration services in Singapore.

**Your Profile:**
- Name: {ICP_NAME}
- Age: {ICP_AGE}
- Nationality: {ICP_NATIONALITY}
- Current Visa: {ICP_CURRENT_VISA}
- Application Type: {ICP_APPLICATION_TYPE}
- Eligibility Status: {ICP_ELIGIBILITY_STATUS}
- Phone Number: {ICP_PHONE_NUMBER}

**Your Details:**
{ICP_FULL_PROFILE}

**Instructions:**
1. Answer questions naturally and conversationally as if you are this person
2. Use information from your profile when answering
3. Be honest and straightforward - don't make up information not in your profile
4. If you don't know something, say "I'm not sure" or "I don't have that information"
5. Show appropriate emotions - excitement if eligible, concern if not eligible
6. When the agent offers to book an appointment:
   - WAIT for the agent to ask "What day/time works for you?" or similar
   - Only AFTER being asked, respond with:
     a) Your preference: phone call OR in-person meeting at the office
     b) A specific day/time preference
   - Example: "I'd prefer a phone call. Monday morning around 10am works great"
   - Example: "Can we meet in person at your office? Tuesday afternoon at 2pm would be good"
   - If ELIGIBLE: Accept enthusiastically with your meeting type preference and time
   - If NOT ELIGIBLE: Still show interest but ask if consultation can help your situation
7. Keep responses brief and natural (1-3 sentences max)
8. Don't reveal that you're an AI - stay in character as {ICP_NAME}
9. Your phone number is {ICP_PHONE_NUMBER} - the agent will use this to create the calendar booking

**Goal:** Have a natural conversation with the immigration consultant (Ava) and book a consultation appointment. Specify whether you want a phone call or in-person meeting.`);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  // Testing state
  const [currentSession, setCurrentSession] = useState<TestSession | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [testingAll, setTestingAll] = useState(false);
  const [currentICPIndex, setCurrentICPIndex] = useState(0);
  const [completedTests, setCompletedTests] = useState(0);
  const [totalTests, setTotalTests] = useState(0);

  // Batch testing state
  const [currentBatch, setCurrentBatch] = useState<BatchInfo | null>(null);
  const [batchStats, setBatchStats] = useState<BatchStats[]>([]);
  const [showBatches, setShowBatches] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);

  // Active batches monitoring state
  const [activeBatches, setActiveBatches] = useState<ActiveBatch[]>([]);
  const [showActiveBatches, setShowActiveBatches] = useState(true);
  const [loadingActiveBatches, setLoadingActiveBatches] = useState(false);
  const [activeBatchesInitialLoad, setActiveBatchesInitialLoad] = useState(true);
  const activeBatchesIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Typing/Chunking settings (EDITABLE for testing, loaded from agent config as defaults)
  const [testSettings, setTestSettings] = useState<{
    typing_burst_enabled: boolean;
    typing_burst_min: number;
    typing_burst_max: number;
    typing_speed_cps: number;
    max_questions_per_response: number;
    max_response_length: number;
    strip_markdown_bold: boolean;
    conversation_history_days: number;
    enable_post_processing: boolean;
    icp_client_max_response_length: number; // ICP Client (roleplaying customer) max message length in CHARACTERS
    enabled_tools: {
      rag_search: boolean;
      calendar_booking: boolean;
      calendar_check: boolean;
      crm_leads: boolean;
      human_handoff: boolean;
    };
  } | null>(null);

  // Timeout timer
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartRef = useRef<number>(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageCountRef = useRef<number>(0);
  const moveToNextICPRef = useRef<(() => Promise<void>) | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // CRITICAL: Refs to track current values across remounts and closures
  // React state gets cleared on component remount, but refs persist
  // This prevents setTimeout callbacks from failing when App component remounts
  // Also prevents stale closures from using old state values
  const currentSessionRef = useRef<TestSession | null>(null);
  const testingAllRef = useRef<boolean>(false);
  const icpsRef = useRef<ICP[]>([]);
  const icpSelectionsRef = useRef<Record<string, boolean>>({});
  const currentICPIndexRef = useRef<number>(0);
  const currentBatchRef = useRef<BatchInfo | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load ICPs and agents on mount
  useEffect(() => {
    loadICPs();
    loadAgents();
    loadSystemPrompt();
  }, []);

  // Load agent config and ICP selections when agent selection changes
  useEffect(() => {
    if (selectedAgent) {
      loadAgentConfig(selectedAgent);
      loadICPSelections(selectedAgent);
    }
  }, [selectedAgent]);

  // Load ICP selections when ICPs are loaded (for initial setup)
  useEffect(() => {
    if (icps.length > 0 && selectedAgent && !icpSelectionsLoaded) {
      loadICPSelections(selectedAgent);
    }
  }, [icps, selectedAgent]);

  // ═══════════════════════════════════════════════════════════════
  // BEFOREUNLOAD WARNING: Prevent accidental page navigation during tests
  // ═══════════════════════════════════════════════════════════════
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    // Only warn if there's an active test session
    if (currentSession && currentSession.session_id) {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires setting returnValue
      return ''; // Other browsers
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentSession]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (activeBatchesIntervalRef.current) {
        clearInterval(activeBatchesIntervalRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, []);

  // CRITICAL: Sync refs with state to avoid stale closures
  // This ensures setTimeout callbacks and moveToNextICP use current values
  useEffect(() => {
    currentSessionRef.current = currentSession;
  }, [currentSession]);

  useEffect(() => {
    testingAllRef.current = testingAll;
  }, [testingAll]);

  useEffect(() => {
    icpsRef.current = icps;
  }, [icps]);

  useEffect(() => {
    icpSelectionsRef.current = icpSelections;
  }, [icpSelections]);

  useEffect(() => {
    currentICPIndexRef.current = currentICPIndex;
  }, [currentICPIndex]);

  useEffect(() => {
    currentBatchRef.current = currentBatch;
  }, [currentBatch]);

  // ═══════════════════════════════════════════════════════════════
  // HEARTBEAT MECHANISM: Track frontend activity to detect disconnections
  // ═══════════════════════════════════════════════════════════════
  const sendHeartbeat = async (sessionId: string) => {
    try {
      await axios.post(`${API_BASE}/api/icp-testing/sessions/${sessionId}/heartbeat`, {
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('[ICP Testing] Failed to send heartbeat:', error);
    }
  };

  useEffect(() => {
    // Start heartbeat when session is active
    if (currentSession && currentSession.session_id && !isPaused) {
      // Send immediate heartbeat on session start
      sendHeartbeat(currentSession.session_id);

      // Send heartbeat every 15 seconds
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat(currentSession.session_id);
      }, 15000);

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };
    } else {
      // Clear heartbeat if no session or paused
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    }
  }, [currentSession, isPaused]);

  const loadSystemPrompt = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/settings/system-prompt`);
      if (response.data.ok) {
        setIcpClientSystemPrompt(response.data.data.system_prompt);
      }
    } catch (error) {
      console.error('Failed to load system prompt from database:', error);
      // Fall back to default if database fails
    }
  };

  const saveTimeout = () => {
    const num = parseInt(timeoutInput);
    if (isNaN(num) || num < 30 || num > 600) {
      setSaveMessage(`❌ Invalid! Must be 30-600 seconds`);
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    localStorage.setItem('icp-test-timeout', timeoutInput);
    setTimeoutSeconds(num);
    setSaveMessage(`✓ Saved! Timeout is now ${timeoutInput}s (${formatTime(num)})`);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Timer countdown
  useEffect(() => {
    if (currentSession && !isPaused) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        const remaining = timeoutSeconds - elapsed;

        if (remaining <= 0) {
          handleTimeout();
        } else {
          setTimeRemaining(remaining);
        }
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [currentSession, isPaused, timeoutSeconds]);

  // Don't merge chunks - keep all messages separate to match audit logs
  const filterChunks = (msgs: Message[]): Message[] => {
    if (msgs.length === 0) return [];

    const filtered: Message[] = [];
    const seenMessageIds = new Set<number>();

    for (let i = 0; i < msgs.length; i++) {
      const msg = msgs[i];

      // Skip if we've already processed this message (by DB ID)
      if (msg.id && seenMessageIds.has(msg.id)) {
        continue;
      }
      if (msg.id) {
        seenMessageIds.add(msg.id);
      }

      // Add ALL messages (user and agent) as separate messages
      filtered.push(msg);
    }

    return filtered;
  };

  // Poll for session updates
  useEffect(() => {
    if (currentSession && !isPaused) {
      // Poll every 1 second for new messages and status updates
      pollingRef.current = setInterval(async () => {
        try {
          const response = await axios.get(`${API_BASE}/api/icp-testing/sessions/${currentSession.session_id}`);
          if (response.data.ok && response.data.data) {
            const { session, messages: serverMessages } = response.data.data;

            // CRITICAL: Check if session is completed/stopped/failed and move to next ICP if batch testing
            if (session.status !== 'in_progress') {
              // Session ended - if batch testing, move to next ICP
              if (testingAll && session.status === 'completed') {
                console.log('[Batch Testing] Session completed, moving to next ICP');
                // Clear polling before moving to next
                if (pollingRef.current) {
                  clearInterval(pollingRef.current);
                  pollingRef.current = null;
                }
                // Move to next ICP using ref to avoid stale closure
                if (moveToNextICPRef.current) {
                  await moveToNextICPRef.current();
                }
                return;
              }
            }

            // Update session info if status changed
            if (session.status !== currentSession.status) {
              setCurrentSession(prev => prev ? { ...prev, status: session.status } : null);
            }

            // Process messages if available
            if (serverMessages) {
              // Only add NEW messages from server (by ID), don't overwrite local state
              setMessages(prev => {
                // Get IDs we've already seen (from merged messages)
                const seenIds = new Set<number>();
                prev.forEach(msg => {
                  if (msg.message_ids) {
                    msg.message_ids.forEach(id => seenIds.add(id));
                  }
                  if (msg.id) {
                    seenIds.add(msg.id);
                  }
                });

                // Filter to only new messages we haven't seen
                const newMessages = serverMessages.filter((msg: any) =>
                  msg.id && !seenIds.has(msg.id)
                );

                if (newMessages.length > 0) {
                  const updated = filterChunks([...prev, ...newMessages]);
                  lastMessageCountRef.current = updated.length;
                  return updated;
                }

                // No new messages
                return prev;
              });
            }
          }
        } catch (error) {
          console.error('Failed to poll for session updates:', error);
        }
      }, 1000);

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    } else {
      // Reset message count when session ends
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      lastMessageCountRef.current = 0;
    }
  }, [currentSession, isPaused, testingAll]);

  const loadICPs = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/icps`);
      if (response.data.ok) {
        setIcps(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load ICPs:', error);
      alert('Failed to load ICPs');
    }
  };

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/agents`);
      if (response.data.ok) {
        setAgents(response.data.data);

        // Auto-select Ava v5 or WhatsApp Sales Assistant v5 if available
        const v5 = response.data.data.find(
          (a: AgentVersion) => a.version === 5 && (a.agent_name === 'Ava' || a.agent_name === 'WhatsApp Sales Assistant')
        );
        if (v5) {
          setSelectedAgent(v5.id);
        } else if (response.data.data.length > 0) {
          // Auto-select first agent if no v5 found
          setSelectedAgent(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
      alert('Failed to load agents. Please refresh the page.');
    }
  };

  const loadAgentConfig = async (agentId: string) => {
    try {
      // Fetch agent config which includes typing settings
      const response = await axios.get(`${API_BASE}/api/agent-testing/config?agent_version_id=${agentId}`);
      if (response.data.ok && response.data.data.typing_settings) {
        const typingSettings = response.data.data.typing_settings;
        // Set test settings with agent config as defaults, then override specific values for ICP testing
        setTestSettings({
          typing_burst_enabled: typingSettings.typing_burst_enabled,
          typing_burst_min: 1, // Override: Use 1 for ICP testing (faster message delivery)
          typing_burst_max: typingSettings.typing_burst_max,
          typing_speed_cps: 50, // Default: 50 CPS for ICP testing (maximum speed for rapid testing)
          max_questions_per_response: 2,
          max_response_length: typingSettings.max_response_length || 150,
          strip_markdown_bold: true,
          conversation_history_days: 14,
          enable_post_processing: true,
          icp_client_max_response_length: 300, // Default to 300 characters (~75 tokens)
          enabled_tools: {
            rag_search: true,
            calendar_booking: true,
            calendar_check: true,
            crm_leads: false,
            human_handoff: false
          }
        });
      } else {
        // Set defaults if no typing settings found
        setTestSettings({
          typing_burst_enabled: true,
          typing_burst_min: 1, // ICP testing default
          typing_burst_max: 3,
          typing_speed_cps: 50, // ICP testing default (50 CPS ≈ 600 WPM, maximum speed for testing)
          max_questions_per_response: 2,
          max_response_length: 150,
          icp_client_max_response_length: 300, // Default to 300 characters (~75 tokens)
          strip_markdown_bold: true,
          conversation_history_days: 14,
          enable_post_processing: true,
          enabled_tools: {
            rag_search: true,
            calendar_booking: true,
            calendar_check: true,
            crm_leads: false,
            human_handoff: false
          }
        });
      }
    } catch (error) {
      console.error('Failed to load agent config:', error);
      // Set defaults on error
      setTestSettings({
        typing_burst_enabled: true,
        typing_burst_min: 1, // ICP testing default
        typing_burst_max: 3,
        typing_speed_cps: 50, // ICP testing default (50 CPS ≈ 600 WPM, maximum speed for testing)
        max_questions_per_response: 2,
        max_response_length: 150,
        icp_client_max_response_length: 75, // Default to 75 tokens (~300 chars)
        strip_markdown_bold: true,
        conversation_history_days: 14,
        enable_post_processing: true,
        enabled_tools: {
          rag_search: true,
          calendar_booking: true,
          calendar_check: true,
          crm_leads: false,
          human_handoff: false
        }
      });
    }
  };

  const loadICPSelections = async (agentId: string) => {
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/selections/${agentId}`);
      if (response.data.ok) {
        // Convert array to map for easier lookups
        const selectionsMap: Record<string, boolean> = {};
        response.data.data.forEach((item: any) => {
          selectionsMap[item.icp_filename] = item.is_selected;
        });
        setIcpSelections(selectionsMap);
        setIcpSelectionsLoaded(true);
        console.log('[ICP Testing] Loaded ICP selections', {
          agentId,
          selectionsCount: Object.keys(selectionsMap).length,
          selectedCount: Object.values(selectionsMap).filter(Boolean).length
        });
      }
    } catch (error) {
      console.error('Failed to load ICP selections:', error);
      // Default to all selected on error
      const allSelected: Record<string, boolean> = {};
      icps.forEach(icp => {
        allSelected[icp.filename] = true;
      });
      setIcpSelections(allSelected);
      setIcpSelectionsLoaded(true);
    }
  };

  const saveICPSelections = async () => {
    if (!selectedAgent) return;

    try {
      const selections = Object.entries(icpSelections).map(([icp_filename, is_selected]) => ({
        icp_filename,
        is_selected
      }));

      const response = await axios.post(`${API_BASE}/api/icp-testing/selections`, {
        agent_version_id: selectedAgent,
        selections
      });

      if (response.data.ok) {
        console.log('[ICP Testing] Saved ICP selections', response.data);
        // Show brief success message
        setSaveMessage(`✓ Saved selection for ${response.data.selected_count} ICPs`);
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Failed to save ICP selections:', error);
      setSaveMessage(`❌ Failed to save selections`);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await axios.get(`${API_BASE}/api/icp-testing/batches`);
      if (response.data.ok) {
        setBatchStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadActiveBatches = async (showLoading = true) => {
    try {
      // Only show loading indicator on initial load or manual refresh
      if (showLoading && activeBatchesInitialLoad) {
        setLoadingActiveBatches(true);
      }
      const response = await axios.get(`${API_BASE}/api/icp-testing/batches/active`);
      if (response.data.ok) {
        setActiveBatches(response.data.data);
      }
      // Mark initial load as complete
      if (activeBatchesInitialLoad) {
        setActiveBatchesInitialLoad(false);
      }
    } catch (error) {
      console.error('Failed to load active batches:', error);
    } finally {
      if (showLoading && activeBatchesInitialLoad) {
        setLoadingActiveBatches(false);
      }
    }
  };

  const stopBatch = async (batchNumber: string) => {
    if (!confirm(`Stop all sessions in batch ${batchNumber}?`)) {
      return;
    }

    try {
      // CRITICAL: If this is the currently running batch, stop frontend processing
      if (currentBatch?.batch_number === batchNumber) {
        setTestingAll(false);
        setCurrentSession(null);
        setMessages([]);
        setTimeRemaining(0);
        setCurrentICPIndex(0);
        setCompletedTests(0);
        setTotalTests(0);
        setCurrentBatch(null);

        // Clear any active timers
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      }

      // Stop backend sessions
      const response = await axios.post(`${API_BASE}/api/icp-testing/batches/${batchNumber}/stop`);
      if (response.data.ok) {
        alert(`Stopped ${response.data.stopped_count} session(s)`);
        await loadActiveBatches();
        await loadBatches();
      }
    } catch (error) {
      console.error('Failed to stop batch:', error);
      alert('Failed to stop batch');
    }
  };

  const stopAllActiveBatches = async () => {
    if (activeBatches.length === 0) {
      alert('No active batches to stop');
      return;
    }

    const totalSessions = activeBatches.reduce((sum, batch) => sum + batch.active_sessions, 0);
    if (!confirm(`Stop all ${activeBatches.length} active batch(es) with ${totalSessions} active session(s)?`)) {
      return;
    }

    try {
      // CRITICAL: Stop frontend batch processing FIRST
      setTestingAll(false);
      setCurrentSession(null);
      setMessages([]);
      setTimeRemaining(0);
      setCurrentICPIndex(0);
      setCompletedTests(0);
      setTotalTests(0);
      setCurrentBatch(null);

      // Clear any active timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      // Then stop backend sessions
      let stoppedCount = 0;
      for (const batch of activeBatches) {
        const response = await axios.post(`${API_BASE}/api/icp-testing/batches/${batch.batch_number}/stop`);
        if (response.data.ok) {
          stoppedCount += response.data.stopped_count;
        }
      }
      alert(`Stopped ${stoppedCount} session(s) across ${activeBatches.length} batch(es)`);
      await loadActiveBatches();
      await loadBatches();
    } catch (error) {
      console.error('Failed to stop all batches:', error);
      alert('Failed to stop some batches');
    }
  };

  // Auto-refresh active batches every 3 seconds
  useEffect(() => {
    loadActiveBatches(true); // Show loading on initial load
    activeBatchesIntervalRef.current = setInterval(() => {
      loadActiveBatches(false); // Don't show loading on auto-refresh
    }, 3000);

    return () => {
      if (activeBatchesIntervalRef.current) {
        clearInterval(activeBatchesIntervalRef.current);
      }
    };
  }, []);

  const startSession = async (icpFilename: string, batchNumber?: string) => {
    if (!selectedAgent) {
      alert('Please select an agent');
      return;
    }

    if (!testSettings) {
      alert('Test settings not loaded. Please wait a moment and try again.');
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(`${API_BASE}/api/icp-testing/sessions/start`, {
        agent_version_id: selectedAgent,
        icp_filename: icpFilename,
        timeout_seconds: timeoutSeconds,
        batch_number: batchNumber, // Pass batch number if provided
        // User-modifiable test settings
        max_questions_per_response: testSettings.max_questions_per_response,
        max_response_length: testSettings.max_response_length,
        strip_markdown_bold: testSettings.strip_markdown_bold,
        conversation_history_days: testSettings.conversation_history_days,
        enable_post_processing: testSettings.enable_post_processing,
        typing_burst_enabled: testSettings.typing_burst_enabled,
        typing_burst_min: testSettings.typing_burst_min,
        typing_burst_max: testSettings.typing_burst_max,
        typing_speed_cps: testSettings.typing_speed_cps,
        icp_client_max_response_length: Math.round(testSettings.icp_client_max_response_length / 4), // Convert chars to tokens
        enabled_tools: JSON.stringify(testSettings.enabled_tools)
      });

      if (response.data.ok) {
        setCurrentSession(response.data.data);
        setMessages([]);
        // conversation history tracking removed
        setTimeRemaining(timeoutSeconds);
        sessionStartRef.current = Date.now();

        // Generate and send initial message
        await sendInitialMessage(response.data.data.session_id, icpFilename);
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      // Only show alert for manual testing, not batch testing
      if (!testingAll) {
        alert('Failed to start test session');
      } else {
        // For batch testing, move to next ICP on error
        await moveToNextICPRef.current?.();
      }
    } finally {
      setIsLoading(false);
      // Clear loading timeout since session started (or failed)
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }
  };

  const sendInitialMessage = async (sessionId: string, icpFilename: string) => {
    try {
      // Load ICP to generate initial message
      const icpResponse = await axios.get(`${API_BASE}/api/icp-testing/icps/${icpFilename}`);
      const profile = icpResponse.data.data.profile;

      const applicationType = profile.profile_type.split(' - ')[0].trim().toLowerCase();
      const greetings = ['hi i need help with', 'hello can you help me with'];
      const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)];

      const appTypeMap: Record<string, string> = {
        'pr': 'pr',
        'citizenship': 'citizenship',
        'ltvp': 'ltvp',
        'pmla': 'pmla',
        'employment pass': 'employment pass',
        'dependent pass': 'dependent pass',
        'stvp': 'visit pass'
      };

      let appType = 'immigration';
      for (const [key, value] of Object.entries(appTypeMap)) {
        if (applicationType.includes(key)) {
          appType = value;
          break;
        }
      }

      const initialMessage = `${randomGreeting} ${appType}`;

      // Send to agent (let polling handle the display)
      const response = await axios.post(
        `${API_BASE}/api/icp-testing/sessions/${sessionId}/message`,
        {
          message: initialMessage,
          conversation_history: []
        }
      );

      if (response.data.ok) {
        // Continue conversation automatically after 2 seconds
        if (!response.data.data.appointment_booked) {
          setTimeout(async () => {
            // CRITICAL: Check if session is still active before continuing
            // Use ref instead of closure variable to handle component remounts
            const session = currentSessionRef.current;
            if (!session || session.session_id !== sessionId) {
              return;
            }
            try {
              await continueConversation(sessionId, icpFilename, response.data.data.agent_message);
            } catch (e) {
              console.error('[ICP Testing] Error in setTimeout continueConversation:', e);
            }
          }, 2000);
        }
      }
    } catch (error: any) {
      // Check if this is a session completed/stopped error (expected during batch transitions)
      const isSessionEndedError =
        error?.response?.status === 400 &&
        error?.response?.data?.error?.includes('cannot send message');

      if (isSessionEndedError) {
        // If batch testing, move to next ICP
        if (testingAllRef.current) {
          await moveToNextICPRef.current?.();
        }
      } else {
        console.error('Failed to send initial message:', error);
        // If batch testing, move to next ICP even on error
        if (testingAllRef.current) {
          await moveToNextICPRef.current?.();
        }
      }
    }
  };

  const continueConversation = async (sessionId: string, icpFilename: string, lastAgentMessage: string) => {
    // CRITICAL: Check if session was stopped
    // Use ref instead of closure variable to handle component remounts
    const session = currentSessionRef.current;
    if (!session || session.session_id !== sessionId || isPaused) {
      return;
    }

    try {
      // Fetch conversation history from database (not from frontend state)
      const sessionResponse = await axios.get(`${API_BASE}/api/icp-testing/sessions/${sessionId}`);

      if (!sessionResponse.data.ok) {
        console.error('[ICP Testing] Failed to fetch session history');
        return;
      }

      // Build conversation history from database messages
      const history = sessionResponse.data.data.messages.map((m: any) => ({
        role: m.role,
        content: m.message
      }));

      // Generate ICP response using LLM
      const icpResponse = await axios.post(`${API_BASE}/api/icp-testing/icp-client-generate`, {
        icp_filename: icpFilename,
        system_prompt: icpClientSystemPrompt,
        agent_message: lastAgentMessage,
        conversation_history: history,
        icp_client_max_tokens: Math.round((testSettings?.icp_client_max_response_length || 300) / 4) // Convert chars to tokens
      });

      if (!icpResponse.data.ok) {
        console.error('[ICP Testing] Failed to generate ICP response:', icpResponse.data.error);
        return;
      }

      const userResponse = icpResponse.data.data.response;

      // Send to backend (let polling handle the display)
      const response = await axios.post(
        `${API_BASE}/api/icp-testing/sessions/${sessionId}/message`,
        {
          message: userResponse,
          conversation_history: history
        }
      );

      if (response.data.ok) {
        // Check if appointment booked
        if (response.data.data.appointment_booked) {
          console.log('[ICP Testing] Appointment booked, completing session and moving to next ICP', {
            testingAll: testingAllRef.current,
            sessionId
          });
          await axios.post(`${API_BASE}/api/icp-testing/sessions/${sessionId}/complete`);
          if (testingAllRef.current) {
            await moveToNextICPRef.current?.();
          } else {
            setCurrentSession(null);
            setMessages([]);
            setTimeRemaining(0);
          }
        } else {
          // Continue conversation after 2 seconds
          setTimeout(async () => {
            // CRITICAL: Check if session is still active before continuing
            // Use ref instead of closure variable to handle component remounts
            const s = currentSessionRef.current;
            if (!s || s.session_id !== sessionId) {
              return;
            }
            try {
              await continueConversation(sessionId, icpFilename, response.data.data.agent_message);
            } catch (e) {
              console.error('[ICP Testing] Error in setTimeout continueConversation:', e);
            }
          }, 2000);
        }
      }
    } catch (error: any) {
      // Check if this is a session completed/stopped error (expected during batch transitions)
      const isSessionEndedError =
        error?.response?.status === 400 &&
        error?.response?.data?.error?.includes('cannot send message');

      if (isSessionEndedError) {
        // If batch testing, move to next ICP
        if (testingAllRef.current) {
          await moveToNextICPRef.current?.();
        }
      } else {
        console.error('[ICP Testing] Failed to continue conversation:', error);
        // If batch testing, move to next ICP even on error
        if (testingAllRef.current) {
          await moveToNextICPRef.current?.();
        }
      }
    }
  };

  const handleTimeout = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Automatically move to next ICP without alert
    if (testingAllRef.current) {
      await moveToNextICPRef.current?.();
    } else {
      await stopSession();
    }
  };

  const stopSession = async () => {
    // CRITICAL: Stop batch processing when user clicks Stop button
    if (testingAll) {
      setTestingAll(false);
      setCurrentICPIndex(0);
      setCompletedTests(0);
      setTotalTests(0);
      setCurrentBatch(null);
    }

    if (!currentSession) return;

    try {
      await axios.post(`${API_BASE}/api/icp-testing/sessions/${currentSession.session_id}/stop`);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      setCurrentSession(null);
      setMessages([]);
      // conversation history tracking removed
      setTimeRemaining(0);
      setIsPaused(false);
    } catch (error) {
      console.error('Failed to stop session:', error);
    }
  };

  // Complete session function (not used in current automated flow, available for future enhancement)
  // const completeSession = async () => { ... };

  const handlePauseContinue = () => {
    setIsPaused(!isPaused);
  };

  const moveToNextICP = async () => {
    // IMPORTANT: Check if batch processing was stopped (use ref to avoid stale closure)
    if (!testingAllRef.current) {
      console.log('[ICP Testing] moveToNextICP: testingAll is false, stopping');
      return;
    }

    setCompletedTests(prev => prev + 1);

    // Get selected ICPs only (use refs to avoid stale closures)
    const selectedICPs = icpsRef.current.filter(icp => icpSelectionsRef.current[icp.filename] !== false);
    const currentIndex = currentICPIndexRef.current;

    console.log('[ICP Testing] moveToNextICP called', {
      currentICPIndex: currentIndex,
      totalICPs: icpsRef.current.length,
      selectedICPsCount: selectedICPs.length,
      selectedICPsFilenames: selectedICPs.map(icp => icp.filename),
      icpSelectionsKeys: Object.keys(icpSelectionsRef.current).length,
      icpSelectionsSelectedCount: Object.values(icpSelectionsRef.current).filter(Boolean).length,
      nextIndex: currentIndex + 1,
      hasMore: currentIndex + 1 < selectedICPs.length
    });

    if (currentIndex + 1 < selectedICPs.length) {
      setCurrentICPIndex(prev => prev + 1);
      const nextICP = selectedICPs[currentIndex + 1];

      // DON'T clear UI state immediately - keep the conversation window visible
      // Just show a loading state while transitioning
      setIsLoading(true);

      // Clear any existing loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      // Set a maximum 10-second timeout to ensure loading indicator disappears
      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        loadingTimeoutRef.current = null;
      }, 10000);

      // Quick transition to next test
      // IMPORTANT: Pass batch_number so all ICPs in the batch are grouped together
      setTimeout(async () => {
        // Double-check batch processing wasn't stopped during the delay (use ref to avoid stale closure)
        if (testingAllRef.current) {
          await startSession(nextICP.filename, currentBatchRef.current?.batch_number);
        } else {
          setIsLoading(false);
          // Clear loading timeout since we're done
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
        }
      }, 500);
    } else {
      // All tests completed - automatically reset without alert
      setTestingAll(false);
      setCurrentICPIndex(0);
      setCompletedTests(0);
      setTotalTests(0);
      setCurrentSession(null);
      setMessages([]);
      setTimeRemaining(0);
    }
  };

  // Keep ref in sync with latest moveToNextICP function to avoid stale closures
  useEffect(() => {
    moveToNextICPRef.current = moveToNextICP;
  }, [moveToNextICP]);

  const openICPSelection = () => {
    if (!selectedAgent) {
      alert('Please select an agent');
      return;
    }

    if (icps.length === 0) {
      alert('No ICP profiles available');
      return;
    }

    // Toggle the selection modal
    setShowICPSelectionModal(!showICPSelectionModal);
  };

  const startTestAllWithSelectedICPs = async () => {
    if (!selectedAgent) {
      alert('Please select an agent');
      return;
    }

    // Get selected ICPs only
    const selectedICPs = icps.filter(icp => icpSelections[icp.filename] !== false);

    console.log('[ICP Testing] Starting batch test with selected ICPs', {
      totalICPs: icps.length,
      selectedICPsCount: selectedICPs.length,
      selectedICPsFilenames: selectedICPs.map(icp => icp.filename),
      icpSelectionsKeys: Object.keys(icpSelections).length,
      icpSelectionsSelectedCount: Object.values(icpSelections).filter(Boolean).length,
      icpSelections: icpSelections
    });

    if (selectedICPs.length === 0) {
      alert('Please select at least one ICP profile to test');
      return;
    }

    // Close the selection modal
    setShowICPSelectionModal(false);

    try {
      setIsLoading(true);
      const response = await axios.post(`${API_BASE}/api/icp-testing/test-all`, {
        agent_version_id: selectedAgent,
        timeout_seconds: timeoutSeconds
      });

      if (response.data.ok) {
        setCurrentBatch(response.data.data);
        setTestingAll(true);
        setCurrentICPIndex(0);
        setCompletedTests(0);
        setTotalTests(selectedICPs.length); // Use selected count

        // Refresh batches list
        await loadBatches();

        // Start the first SELECTED ICP conversation
        await startSession(selectedICPs[0].filename, response.data.data.batch_number);
      }
    } catch (error: any) {
      console.error('Failed to start batch test:', error);
      alert(`Failed to start batch test: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
      // Clear loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }
  };

  const startTestOne = async () => {
    if (!selectedICP) {
      alert('Please select an ICP');
      return;
    }

    await startSession(selectedICP);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  return (
    <div className="admin-main-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>🧪 ICP Testing - AI Agent Conversation Testing</h1>
        <button
          onClick={() => window.location.href = '/admin'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      <div className="admin-layout-with-sidebar">
        {/* Admin Navigation Sidebar */}
        <div className="admin-tabs-sidebar">
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin'}
          >
            📱 WhatsApp Connection
          </button>

          {/* AI Agents Collapsible Section */}
          <div className="nav-section">
            <button
              className="tab-button section-header active-section"
              onClick={() => setExpandedSections(!expandedSections)}
            >
              <span className="section-title">🤖 AI Agents</span>
              <span className="expand-icon">{expandedSections ? "▼" : "▶"}</span>
            </button>

            {expandedSections && (
              <div className="nav-subsection">
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=ai-models'}
                >
                  ⚙️ AI Model Selection
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=create-agent'}
                >
                  ➕ Create Chat Agent
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=agents'}
                >
                  🤖 Agent Controls
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=system-prompt'}
                >
                  📝 System Prompt
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=knowledge'}
                >
                  📚 Knowledge Base
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=urls'}
                >
                  🌐 URL Management
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=rag'}
                >
                  🧩 RAG Management
                </button>
                <button
                  className="tab-button nested active"
                  onClick={() => window.location.href = '/icp-testing'}
                >
                  🧪 ICP Testing
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/icp-testing/audit'}
                >
                  📊 ICP Testing Audit
                </button>
              </div>
            )}
          </div>

          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=tokens'}
          >
            Token Usage
          </button>
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=llm-config'}
          >
            AI Models
          </button>
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=settings'}
          >
            ⚙️ System Settings
          </button>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1 }}>
          {/* Configuration Panel */}
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3>Test Configuration</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
          {/* Agent Selection */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select Agent:
            </label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
              disabled={!!currentSession}
            >
              <option value="">-- Select Agent --</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.agent_name} v{agent.version} ({agent.stage})
                </option>
              ))}
            </select>
          </div>

          {/* ICP Selection */}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select ICP (for single test):
            </label>
            <select
              value={selectedICP}
              onChange={(e) => setSelectedICP(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
              disabled={!!currentSession}
            >
              <option value="">-- Select ICP --</option>
              {icps.map(icp => (
                <option key={icp.filename} value={icp.filename}>
                  {icp.profile_name} - {icp.profile_type} {icp.eligibility_status === 'NOT ELIGIBLE' ? '❌' : '✅'}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Timeout Configuration */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Timeout (seconds):
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="number"
              value={timeoutInput}
              onChange={(e) => setTimeoutInput(e.target.value)}
              min={30}
              max={600}
              step={10}
              style={{
                width: '200px',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px'
              }}
              disabled={!!currentSession}
            />
            <button
              onClick={saveTimeout}
              style={{
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={!!currentSession}
            >
              💾 Save
            </button>
            <span style={{ color: '#666' }}>
              ({formatTime(timeoutSeconds)})
            </span>
            {saveMessage && (
              <span style={{
                color: '#4CAF50',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                {saveMessage}
              </span>
            )}
          </div>
        </div>

        {/* Test Settings - Editable */}
        {testSettings && (
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '2px solid #4CAF50'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#2E7D32' }}>
              ⚙️ Test Settings (Customizable) <span style={{ fontSize: '14px', color: '#f57c00', fontWeight: 'normal' }}>(⚠️ NOT ACTIVE - Settings from agent version are used instead)</span>
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              <strong>⚠️ Not Active:</strong> These customizable test settings are currently not being used. The system uses settings from the selected AI Agent version (v7.12) instead. We will implement this feature later to allow per-test customization.
            </p>

            {/* ═══════════════════════════════════════════════════════════════════════════════
              ⚠️ TEST SETTINGS SECTION - NOT ACTIVE (Current Implementation)
              ═══════════════════════════════════════════════════════════════════════════════
              The settings below are NOT currently being used in ICP testing.

              CURRENT BEHAVIOR:
              - ICP tests use settings from the selected agent version (e.g., v7.12)
              - Agent version settings include: max_response_length, max_questions_per_response,
                typing_speed_cps, typing_burst_enabled, etc.
              - These settings are loaded from chat_agent_versions table in the database

              TODO: Implement this feature to allow per-test customization:
              - Allow users to override agent version settings for individual tests
              - Pass these settings to backend via /api/icp-testing/auto-test endpoint
              - Backend should use test settings if provided, otherwise fall back to agent version settings

              Related files:
              - Backend: /orchestrator/src/api/icp-testing-endpoints.ts
              - Database: chat_agent_versions table
              ═══════════════════════════════════════════════════════════════════════════════ */}

            {/* Message Chunking & Typing Settings */}
            <div style={{
              backgroundColor: '#fff',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '15px',
              border: '1px solid #ddd'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>💬 Message Chunking & Typing</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                {/* Chunking Enabled */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Enable Chunking
                  </label>
                  <select
                    value={testSettings.typing_burst_enabled.toString()}
                    onChange={(e) => setTestSettings({ ...testSettings, typing_burst_enabled: e.target.value === 'true' })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="true">✅ Enabled</option>
                    <option value="false">❌ Disabled</option>
                  </select>
                </div>

                {/* Min Chunks */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Min Chunks
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={testSettings.typing_burst_min}
                    onChange={(e) => setTestSettings({ ...testSettings, typing_burst_min: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>

                {/* Max Chunks */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Max Chunks
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={testSettings.typing_burst_max}
                    onChange={(e) => setTestSettings({ ...testSettings, typing_burst_max: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>

                {/* Typing Speed */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Speed (CPS)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="0.25"
                    value={testSettings.typing_speed_cps}
                    onChange={(e) => setTestSettings({ ...testSettings, typing_speed_cps: parseFloat(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                    ≈ {Math.round(testSettings.typing_speed_cps * 12)} WPM
                  </div>
                </div>
              </div>
            </div>

            {/* Response Formatting Settings */}
            <div style={{
              backgroundColor: '#fff',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '15px',
              border: '1px solid #ddd'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>📝 Response Formatting</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {/* Max Questions */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Max Questions Per Response
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={testSettings.max_questions_per_response}
                    onChange={(e) => setTestSettings({ ...testSettings, max_questions_per_response: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                    0 = unlimited
                  </div>
                </div>

                {/* Max Response Length (Characters) */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Max Response Length (Characters)
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="5000"
                    step="10"
                    value={testSettings.max_response_length}
                    onChange={(e) => setTestSettings({ ...testSettings, max_response_length: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                    Default: 150 chars
                  </div>
                </div>

                {/* ICP Client Max Response Length (Characters) */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    📱 ICP Client Max Length (Characters)
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="2000"
                    step="50"
                    value={testSettings.icp_client_max_response_length}
                    onChange={(e) => setTestSettings({ ...testSettings, icp_client_max_response_length: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                    ~{Math.round(testSettings.icp_client_max_response_length / 4)} tokens • Roleplaying customer
                  </div>
                </div>

                {/* Conversation History Days */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    📅 Conversation History Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    step="1"
                    value={testSettings.conversation_history_days}
                    onChange={(e) => setTestSettings({ ...testSettings, conversation_history_days: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                    Production only • Default: 14 days
                  </div>
                </div>

                {/* Strip Markdown Bold */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Strip **Bold** Markdown
                  </label>
                  <select
                    value={testSettings.strip_markdown_bold.toString()}
                    onChange={(e) => setTestSettings({ ...testSettings, strip_markdown_bold: e.target.value === 'true' })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="true">✅ Strip</option>
                    <option value="false">❌ Keep</option>
                  </select>
                </div>

                {/* Enable Post Processing */}
                <div>
                  <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Post-Processing
                  </label>
                  <select
                    value={testSettings.enable_post_processing.toString()}
                    onChange={(e) => setTestSettings({ ...testSettings, enable_post_processing: e.target.value === 'true' })}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="true">✅ Enabled</option>
                    <option value="false">❌ Disabled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Tool Selection */}
            <div style={{
              backgroundColor: '#fff',
              padding: '15px',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>🔧 Tool Selection</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                {[
                  { key: 'rag_search' as const, label: 'RAG Search', icon: '🔍' },
                  { key: 'calendar_booking' as const, label: 'Calendar Booking', icon: '📅' },
                  { key: 'calendar_check' as const, label: 'Calendar Check', icon: '📆' },
                  { key: 'crm_leads' as const, label: 'CRM Leads', icon: '👥' },
                  { key: 'human_handoff' as const, label: 'Human Handoff', icon: '👨‍💼' }
                ].map(tool => (
                  <label key={tool.key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    backgroundColor: testSettings.enabled_tools[tool.key] ? '#E8F5E9' : '#f5f5f5',
                    borderRadius: '4px',
                    border: `1px solid ${testSettings.enabled_tools[tool.key] ? '#4CAF50' : '#ddd'}`,
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}>
                    <input
                      type="checkbox"
                      checked={testSettings.enabled_tools[tool.key]}
                      onChange={(e) => setTestSettings({
                        ...testSettings,
                        enabled_tools: {
                          ...testSettings.enabled_tools,
                          [tool.key]: e.target.checked
                        }
                      })}
                      style={{ marginRight: '8px' }}
                    />
                    <span>{tool.icon} {tool.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ICP Client Agent System Prompt Editor */}
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #ddd'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>🤖 ICP Client Agent Configuration</h3>
            <button
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              style={{
                padding: '5px 15px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {showPromptEditor ? '▼ Hide' : '▶ Show'} System Prompt
            </button>
          </div>

          {showPromptEditor && (
            <div>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                This system prompt instructs the ICP client agent how to roleplay as the customer.
                Available placeholders: {'{ICP_NAME}'}, {'{ICP_AGE}'}, {'{ICP_NATIONALITY}'}, {'{ICP_CURRENT_VISA}'},
                {'{ICP_APPLICATION_TYPE}'}, {'{ICP_ELIGIBILITY_STATUS}'}, {'{ICP_FULL_PROFILE}'}
              </p>
              <textarea
                value={icpClientSystemPrompt}
                onChange={(e) => setIcpClientSystemPrompt(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '300px',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  resize: 'vertical'
                }}
                disabled={!!currentSession}
              />
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.post(`${API_BASE}/api/icp-testing/settings/system-prompt`, {
                        system_prompt: icpClientSystemPrompt
                      });
                      if (response.data.ok) {
                        alert('System prompt saved to database successfully');
                      } else {
                        alert('Failed to save: ' + response.data.error);
                      }
                    } catch (error: any) {
                      alert('Error saving to database: ' + (error.response?.data?.error || error.message));
                    }
                  }}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  disabled={!!currentSession}
                >
                  💾 Save to Database
                </button>
                <button
                  onClick={async () => {
                    await loadSystemPrompt();
                    alert('System prompt reloaded from database');
                  }}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  disabled={!!currentSession}
                >
                  📂 Reload from Database
                </button>
                <button
                  onClick={() => {
                    if (confirm('Reset to default system prompt?')) {
                      setIcpClientSystemPrompt(`You are roleplaying as {ICP_NAME}, a real person seeking immigration services in Singapore.

**Your Profile:**
- Name: {ICP_NAME}
- Age: {ICP_AGE}
- Nationality: {ICP_NATIONALITY}
- Current Visa: {ICP_CURRENT_VISA}
- Application Type: {ICP_APPLICATION_TYPE}
- Eligibility Status: {ICP_ELIGIBILITY_STATUS}

**Your Details:**
{ICP_FULL_PROFILE}

**Instructions:**
1. Answer questions naturally and conversationally as if you are this person
2. Use information from your profile when answering
3. Be honest and straightforward - don't make up information not in your profile
4. If you don't know something, say "I'm not sure" or "I don't have that information"
5. Show appropriate emotions - excitement if eligible, concern if not eligible
6. When the agent offers to book an appointment:
   - WAIT for the agent to ask "What day/time works for you?" or similar
   - Only AFTER being asked, respond with a specific preference like "Monday morning around 10am works great"
   - If ELIGIBLE: Accept enthusiastically with a specific time preference
   - If NOT ELIGIBLE: Still show interest but ask if consultation can help your situation
7. Keep responses brief and natural (1-3 sentences max)
8. Don't reveal that you're an AI - stay in character as {ICP_NAME}

**Goal:** Have a natural conversation with the immigration consultant (Ava) and book a consultation appointment.`);
                    }
                  }}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#607D8B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  disabled={!!currentSession}
                >
                  🔄 Reset to Default
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={openICPSelection}
            disabled={!!currentSession || isLoading || icps.length === 0 || testingAll}
            title="Select ICPs to test in batch"
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !!currentSession || isLoading || testingAll ? 'not-allowed' : 'pointer',
              opacity: !!currentSession || isLoading || testingAll ? 0.5 : 1
            }}
          >
            🚀 Test ICPs ({icps.filter(icp => icpSelections[icp.filename] !== false).length}/{icps.length})
          </button>

          <button
            onClick={startTestOne}
            disabled={!!currentSession || isLoading || !selectedICP || testingAll}
            title="Run automated conversation test with the selected ICP profile"
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !!currentSession || isLoading || !selectedICP || testingAll ? 'not-allowed' : 'pointer',
              opacity: !!currentSession || isLoading || !selectedICP || testingAll ? 0.5 : 1
            }}
          >
            🔬 Test Selected ICP
          </button>

          {currentSession && (
            <>
              <button
                onClick={handlePauseContinue}
                title={isPaused ? 'Resume conversation' : 'Pause conversation temporarily'}
                style={{
                  padding: '10px 20px',
                  backgroundColor: isPaused ? '#4CAF50' : '#FF9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {isPaused ? '▶️ Continue' : '⏸️ Pause'}
              </button>

              <button
                onClick={stopSession}
                title={testingAll ? 'Stop entire batch test' : 'Stop current test session'}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ⏹️ Stop
              </button>

              {testingAll && (
                <button
                  onClick={async () => {
                    // Stop current session and move to next ICP
                    if (currentSession) {
                      try {
                        await axios.post(`${API_BASE}/api/icp-testing/sessions/${currentSession.session_id}/stop`);
                      } catch (error) {
                        console.error('Failed to stop current session:', error);
                      }
                    }
                    await moveToNextICPRef.current?.();
                  }}
                  title="Skip to next ICP in batch"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#9C27B0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ⏭️ Skip
                </button>
              )}
            </>
          )}

          <button
            onClick={() => window.location.href = '/icp-testing/audit'}
            title="View detailed audit logs and analytics for all test sessions"
            style={{
              padding: '10px 20px',
              backgroundColor: '#607D8B',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            📊 View Audit Log
          </button>
        </div>

        {/* ICP Selection Modal */}
        {showICPSelectionModal && (
          <div style={{
            marginTop: '15px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #4CAF50'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>
                📋 Select ICPs to Test ({icps.filter(icp => icpSelections[icp.filename] !== false).length}/{icps.length} selected)
              </h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => {
                    const allSelected: Record<string, boolean> = {};
                    icps.forEach(icp => {
                      allSelected[icp.filename] = true;
                    });
                    setIcpSelections(allSelected);
                  }}
                  style={{
                    padding: '5px 15px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ✓ Select All
                </button>
                <button
                  onClick={() => {
                    const allDeselected: Record<string, boolean> = {};
                    icps.forEach(icp => {
                      allDeselected[icp.filename] = false;
                    });
                    setIcpSelections(allDeselected);
                  }}
                  style={{
                    padding: '5px 15px',
                    backgroundColor: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ✗ Deselect All
                </button>
              </div>
            </div>

            {/* Scrollable checkbox list */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: 'white',
              padding: '15px',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              {icps.map((icp) => (
                <label
                  key={icp.filename}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    marginBottom: '5px',
                    backgroundColor: icpSelections[icp.filename] !== false ? '#e8f5e9' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (icpSelections[icp.filename] === false) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (icpSelections[icp.filename] === false) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={icpSelections[icp.filename] !== false}
                    onChange={(e) => {
                      setIcpSelections({
                        ...icpSelections,
                        [icp.filename]: e.target.checked
                      });
                    }}
                    style={{
                      marginRight: '10px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: '#333' }}>{icp.profile_name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{icp.filename}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' }}>
              {saveMessage && (
                <span style={{
                  padding: '10px',
                  color: saveMessage.includes('✓') ? '#4CAF50' : '#f44336',
                  fontSize: '14px',
                  alignSelf: 'center'
                }}>
                  {saveMessage}
                </span>
              )}
              <button
                onClick={saveICPSelections}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                💾 Save Selections
              </button>
              <button
                onClick={startTestAllWithSelectedICPs}
                disabled={(() => {
                  // Count ICPs that are NOT explicitly false (undefined = selected by default)
                  const selectedCount = icps.filter(icp => icpSelections[icp.filename] !== false).length;
                  return selectedCount === 0;
                })()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (() => {
                    const selectedCount = icps.filter(icp => icpSelections[icp.filename] !== false).length;
                    return selectedCount === 0 ? 'not-allowed' : 'pointer';
                  })(),
                  opacity: (() => {
                    const selectedCount = icps.filter(icp => icpSelections[icp.filename] !== false).length;
                    return selectedCount === 0 ? 0.5 : 1;
                  })()
                }}
              >
                🚀 Start Test ({icps.filter(icp => icpSelections[icp.filename] !== false).length} ICPs)
              </button>
              <button
                onClick={() => setShowICPSelectionModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#757575',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar for Test All */}
      {testingAll && totalTests > 0 && (
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontWeight: 'bold' }}>
              Testing Progress: {completedTests + 1} / {totalTests}
            </span>
            <span style={{ color: '#666' }}>
              Current: {icps[currentICPIndex]?.profile_name}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '20px',
            backgroundColor: '#e0e0e0',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${((completedTests + 1) / totalTests) * 100}%`,
              height: '100%',
              backgroundColor: '#4CAF50',
              transition: 'width 0.3s ease'
            }} />
          </div>
          {currentBatch && (
            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
              Batch: <strong>{currentBatch.batch_number}</strong>
            </div>
          )}
        </div>
      )}

      {/* Active Batches Section - Hide during active session to avoid blocking conversation view */}
      {showActiveBatches && !currentSession && (
        <div style={{
          backgroundColor: activeBatches.length > 0 ? '#fff3cd' : '#f8f9fa',
          border: activeBatches.length > 0 ? '2px solid #ffc107' : '1px solid #dee2e6',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              {activeBatches.length > 0 && <span style={{ animation: 'pulse 1.5s infinite' }}>🔴</span>}
              Active Batches {activeBatches.length > 0 && `(${activeBatches.length})`}
            </h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              {activeBatches.length > 0 && (
                <button
                  onClick={stopAllActiveBatches}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#D32F2F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  🛑 Stop All Batches
                </button>
              )}
              <button
                onClick={() => setShowActiveBatches(false)}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {loadingActiveBatches ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading active batches...</div>
          ) : activeBatches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              ✅ No active batches running
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fff8e1' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ffc107' }}>Batch #</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ffc107' }}>Active Sessions</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ffc107' }}>Completed</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ffc107' }}>Total</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ffc107' }}>Started At</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ffc107' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeBatches.map((batch) => (
                    <tr
                      key={batch.batch_number}
                      style={{
                        backgroundColor: 'white',
                        borderBottom: '1px solid #ffc107'
                      }}
                    >
                      <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {batch.batch_number}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span style={{
                          color: '#D32F2F',
                          fontWeight: 'bold',
                          fontSize: '1.1em'
                        }}>
                          {batch.active_sessions}
                        </span>
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {batch.completed_count}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {batch.total_sessions}
                      </td>
                      <td style={{ padding: '10px', fontSize: '0.9em' }}>
                        {new Date(batch.started_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => stopBatch(batch.batch_number)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#D32F2F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9em'
                          }}
                        >
                          Stop
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '10px', fontSize: '0.85em', color: '#666', textAlign: 'center' }}>
                ⏱️ Auto-refreshes every 3 seconds
              </div>
            </div>
          )}
        </div>
      )}

      {!showActiveBatches && !currentSession && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <button
            onClick={() => setShowActiveBatches(true)}
            style={{
              padding: '8px 15px',
              backgroundColor: '#ffc107',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🔴 Show Active Batches
          </button>
        </div>
      )}

      {/* Batch Audit Section */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>📊 Batch Audit Log</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={loadBatches}
              disabled={loadingBatches}
              style={{
                padding: '8px 15px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingBatches ? 'not-allowed' : 'pointer',
                opacity: loadingBatches ? 0.5 : 1
              }}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => setShowBatches(!showBatches)}
              style={{
                padding: '8px 15px',
                backgroundColor: '#607D8B',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showBatches ? '▼ Hide' : '▶ Show'} Batches
            </button>
          </div>
        </div>

        {showBatches && (
          <div>
            {loadingBatches ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading batches...</div>
            ) : batchStats.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                No batch tests found. Click "Test All ICPs" to create a batch.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Batch #</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Sessions</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Completed</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Appointments</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>First Session</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Last Session</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchStats.map((batch) => (
                      <tr
                        key={batch.batch_number}
                        style={{
                          backgroundColor: 'white',
                          borderBottom: '1px solid #eee'
                        }}
                      >
                        <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                          {batch.batch_number}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          {batch.session_count}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{
                            color: batch.completed_count === batch.session_count ? '#4CAF50' : '#FF9800',
                            fontWeight: 'bold'
                          }}>
                            {batch.completed_count}/{batch.session_count}
                          </span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{
                            color: batch.appointment_count > 0 ? '#4CAF50' : '#666',
                            fontWeight: batch.appointment_count > 0 ? 'bold' : 'normal'
                          }}>
                            {batch.appointment_count}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '0.85em', color: '#666' }}>
                          {formatDate(batch.first_session)}
                        </td>
                        <td style={{ padding: '10px', fontSize: '0.85em', color: '#666' }}>
                          {formatDate(batch.last_session)}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            onClick={() => window.open(`/icp-testing/audit?batch=${batch.batch_number}`, '_blank')}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.9em'
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conversation Display */}
      {currentSession && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '300px 1fr',
          gap: '20px'
        }}>
          {/* Session Info Sidebar */}
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            height: 'fit-content'
          }}>
            <h3 style={{ marginTop: 0 }}>Session Info</h3>

            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Profile:</div>
              <div>{currentSession.profile.name}</div>
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                {currentSession.profile.type}
              </div>
              <div style={{ fontSize: '0.9em', marginTop: '5px' }}>
                {currentSession.profile.eligibility === 'NOT ELIGIBLE' ? '❌' : '✅'} {currentSession.profile.eligibility}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Time Remaining:</div>
              <div style={{
                fontSize: '2em',
                fontWeight: 'bold',
                color: timeRemaining < 30 ? '#f44336' : '#4CAF50'
              }}>
                {formatTime(timeRemaining)}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Messages:</div>
              <div>{messages.length}</div>
            </div>

            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Status:</div>
              <div style={{
                padding: '5px 10px',
                borderRadius: '4px',
                backgroundColor: isPaused ? '#FF9800' : '#4CAF50',
                color: 'white',
                textAlign: 'center'
              }}>
                {isPaused ? '⏸️ Paused' : '▶️ Active'}
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '20px',
            height: '600px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ marginTop: 0 }}>Conversation</h3>
              {isLoading && (
                <div style={{
                  padding: '5px 12px',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}>
                  ⏳ Starting next ICP...
                </div>
              )}
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '15px',
              padding: '10px'
            }}>
              {messages.map((msg, index) => (
                <div
                  key={msg.id ? `msg-${msg.id}` : `msg-${msg.role}-${msg.turn_number}-${index}`}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: '15px'
                  }}
                >
                  <div style={{
                    maxWidth: '70%',
                    padding: '10px 15px',
                    borderRadius: '12px',
                    backgroundColor: msg.role === 'user' ? '#2196F3' : '#f5f5f5',
                    color: msg.role === 'user' ? 'white' : 'black'
                  }}>
                    <div style={{ fontSize: '0.8em', marginBottom: '5px', opacity: 0.8 }}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Agent'} • Turn {msg.turn_number}
                      {msg.role === 'agent' && msg.chunk_count && msg.chunk_count > 1 && (
                        <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                          ({msg.turn_range?.[0]}-{msg.turn_range?.[1]}, {msg.chunk_count} chunks)
                        </span>
                      )}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                    {/* Timestamp footer */}
                    <div style={{
                      fontSize: '0.7em',
                      marginTop: '8px',
                      paddingTop: '6px',
                      borderTop: `1px solid ${msg.role === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
                      opacity: 0.7,
                      fontFamily: 'monospace'
                    }}>
                      {(() => {
                        const timestamp = new Date(msg.timestamp);
                        const hours = timestamp.getHours().toString().padStart(2, '0');
                        const minutes = timestamp.getMinutes().toString().padStart(2, '0');
                        const seconds = timestamp.getSeconds().toString().padStart(2, '0');

                        // Calculate time since previous message
                        let timeSincePrev = '';
                        if (index > 0) {
                          const prevTimestamp = new Date(messages[index - 1].timestamp);
                          const diffMs = timestamp.getTime() - prevTimestamp.getTime();
                          const diffSec = Math.round(diffMs / 1000);
                          timeSincePrev = ` (+${diffSec}s)`;
                        }

                        return `${hours}:${minutes}:${seconds}${timeSincePrev}`;
                      })()}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Manual message input (optional) */}
            <div style={{
              borderTop: '1px solid #ddd',
              paddingTop: '15px'
            }}>
              <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                💡 Conversation is automated based on ICP profile. Pause to inspect or stop to end.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ICP List */}
      {!currentSession && (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          marginTop: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>Available ICPs ({icps.length})</h3>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Profile Name</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Type</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Application</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nationality</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {icps.map((icp, index) => (
                  <tr
                    key={icp.filename}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSelectedICP(icp.filename)}
                  >
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                      {icp.profile_name}
                    </td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '0.9em' }}>
                      {icp.profile_type}
                    </td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                      {icp.application_type}
                    </td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                      {icp.nationality}
                    </td>
                    <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                      {icp.eligibility_status === 'NOT ELIGIBLE' ? (
                        <span style={{ color: '#f44336', fontWeight: 'bold' }}>❌ Not Eligible</span>
                      ) : (
                        <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>✅ Eligible</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
